import React, { useRef, useState } from 'react';
import { useNavigate, Navigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import {
  Package, Truck, Container, Car, ArrowRight, MapPin, Send, Handshake,
  CheckCircle, Eye, Clock, DollarSign, Euro, Trophy, ShieldCheck, Lock,
  Headphones, UserCheck, Star, Zap, Search, MessageCircle, Volume2, VolumeX,
} from 'lucide-react';
import LiveActivityStrip from '../components/LiveActivityStrip';
import InstallAppPrompt from '../components/InstallAppPrompt';
import HomeDirectorySearch from '../components/HomeDirectorySearch';
import { FeatureStrip, BiddingShowcase, CategoryTiles } from '../components/landing/LandingSections';
import { DIRECTORY_ENABLED } from '../config/features';
import { AudienceTabs } from '../components/landing/AudienceTabs';
import { SocialLinks } from '../components/SocialLinks';
import PromoBannerStrip from '../components/PromoBannerStrip';
import useSiteContent from '../hooks/useSiteContent';

export default function LandingPage() {
  const navigate = useNavigate();
  const { t, banners } = useSiteContent();
  const { user } = useAuth();
  const heroVideoRef = useRef(null);
  const [heroMuted, setHeroMuted] = useState(true);

  const toggleHeroSound = () => {
    const el = heroVideoRef.current;
    if (!el) return;
    el.muted = !el.muted;
    setHeroMuted(el.muted);
    if (!el.muted) el.play().catch(() => {});
  };

  if (user) {
    const routes = { admin: '/admin', driver: '/driver', customer: '/customer' };
    return <Navigate to={routes[user.role] || '/login'} replace />;
  }

  const heroVideo = t('hero_video_url', '/justdeliver-explainer.mp4');
  const heroImage = t('hero_image_url', 'https://static.prod-images.emergentagent.com/jobs/48233051-cb93-4ab9-b9b5-4885a455c403/images/6f2a79d5403906f271f8d1f0526ec05992b8ec72e40df8e92b15916c3c0bbb9b.jpeg');

  const heroFeatures = [
    { Icon: Euro, label: 'Drivers bid their best price' },
    { Icon: Trophy, label: 'You choose the best offer' },
    { Icon: MapPin, label: 'Track in real time' },
    { Icon: ShieldCheck, label: 'Delivered with care' },
  ];

  const howSteps = [
    {
      num: '1',
      title: 'Post Your Delivery',
      desc: 'Tell us what you need delivered, where and when.',
      Visual: () => (
        <div className="rounded-xl bg-emerald-50 p-4 flex items-center justify-center h-28">
          <div className="rounded-lg bg-white shadow-sm px-3 py-2 flex items-center gap-2 ring-1 ring-emerald-100">
            <MapPin className="w-4 h-4 text-[#2E7D32]" />
            <span className="text-xs font-semibold text-[#050505]">Dublin → Kildare</span>
          </div>
        </div>
      ),
    },
    {
      num: '2',
      title: 'Drivers Compete',
      desc: 'Verified drivers place their best bids.',
      Visual: () => (
        <div className="rounded-xl bg-emerald-50 p-4 h-28 flex flex-col justify-center gap-1.5">
          {[
            { p: '€28', best: false },
            { p: '€25', best: true },
            { p: '€23', best: false },
          ].map((b, i) => (
            <div
              key={i}
              className={`flex items-center justify-between rounded-md px-2 py-1 ${
                b.best ? 'bg-white ring-1 ring-[#2E7D32]/40' : 'bg-white/60'
              }`}
            >
              <span className="w-5 h-5 rounded-full bg-gray-200" />
              <span className="text-xs font-bold text-[#050505]">{b.p}</span>
              {b.best && (
                <span className="text-[8px] font-bold uppercase text-[#1B5E20]">Best</span>
              )}
            </div>
          ))}
        </div>
      ),
    },
    {
      num: '3',
      title: 'You Choose',
      desc: 'Pick the offer that suits you best.',
      Visual: () => (
        <div className="rounded-xl bg-emerald-50 p-4 h-28 flex flex-col justify-center">
          <div className="rounded-lg bg-white p-3 ring-1 ring-emerald-100">
            <div className="flex items-center justify-between">
              <span className="text-base font-bold text-[#050505]">€25</span>
              <div className="flex gap-0.5">
                {[1, 2, 3, 4, 5].map((s) => (
                  <Star key={s} className="w-3 h-3 fill-[#F57E0E] text-[#F57E0E]" />
                ))}
              </div>
            </div>
            <div className="mt-1.5 text-center text-[10px] font-bold text-white bg-[#2E7D32] rounded py-1">
              Accept offer
            </div>
          </div>
        </div>
      ),
    },
    {
      num: '4',
      title: 'Delivered Safely',
      desc: 'Track in real time and get your item delivered.',
      Visual: () => (
        <div className="rounded-xl bg-emerald-50 p-4 h-28 flex items-center justify-center relative overflow-hidden">
          <svg viewBox="0 0 120 60" className="w-full h-full">
            <path
              d="M 10 50 Q 40 5, 70 30 T 110 15"
              fill="none"
              stroke="#2E7D32"
              strokeWidth="2"
              strokeDasharray="4,3"
            />
            <circle cx="10" cy="50" r="3" fill="#2E7D32" />
            <circle cx="110" cy="15" r="6" fill="#fff" stroke="#2E7D32" strokeWidth="2" />
          </svg>
          <CheckCircle className="absolute right-3 top-3 w-5 h-5 text-[#2E7D32] bg-white rounded-full" />
        </div>
      ),
    },
  ];

  const trust = [
    { Icon: UserCheck, title: 'Verified Drivers', desc: 'All drivers are vetted and trusted.' },
    { Icon: Lock, title: 'Secure Payments', desc: 'Protected until delivery is complete.' },
    { Icon: MessageCircle, title: 'Chat With Drivers', desc: 'Message drivers about your delivery.' },
    { Icon: Headphones, title: 'Customer Support', desc: "We're here 7 days a week." },
    { Icon: MapPin, title: 'Real-Time Tracking', desc: 'Track every step of the way.' },
  ];

  return (
    <div className="min-h-screen bg-white" style={{ fontFamily: "'Inter', sans-serif" }}>
      {/* Sticky Header */}
      <header className="sticky top-0 z-50 backdrop-blur-xl bg-white/80 border-b border-gray-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-12 flex items-center justify-between h-16 sm:h-20">
          <a href="/" className="flex items-center" data-testid="nav-logo">
            <img src="/logo.png" alt="JustDeliver" className="h-6 sm:h-[33px] md:h-[39px] w-auto" />
          </a>
          <nav className="hidden md:flex items-center gap-2">
            <AudienceTabs />
            {DIRECTORY_ENABLED && (
            <a href="/drivers" className="px-5 py-2.5 text-lg font-bold text-white bg-[#2E7D32] hover:bg-[#1B5E20] rounded-full transition-all duration-150 hover:-translate-y-0.5 hover:shadow-md inline-flex items-center gap-1.5" data-testid="nav-directory">
              <Search className="w-4 h-4" /> Search Drivers
            </a>
            )}
          </nav>
          <div className="flex items-center gap-2">
            <button
              onClick={() => navigate('/login')}
              className="px-4 py-2 text-sm font-bold text-[#050505] hover:bg-gray-100 transition-colors rounded-md"
              data-testid="nav-login-button"
            >
              Log In
            </button>
            <button
              onClick={() => navigate('/register')}
              className="px-4 py-2 text-sm font-bold bg-[#2E7D32] text-white hover:bg-[#1B5E20] transition-all active:scale-95 rounded-md"
              data-testid="nav-register-button"
            >
              Sign Up
            </button>
          </div>
        </div>
        {/* Mobile nav */}
        <div className="md:hidden flex items-center justify-center gap-1.5 pb-2 px-2 flex-wrap">
          <AudienceTabs size="sm" className="w-full flex" />
          {DIRECTORY_ENABLED && (
          <a href="/drivers" className="px-4 py-2 text-base font-bold text-white bg-[#2E7D32] rounded-full inline-flex items-center gap-1.5" data-testid="nav-directory-mobile">
            <Search className="w-4 h-4" /> Search Drivers
          </a>
          )}
        </div>
      </header>

      {/* Promo / announcement strip — managed in the admin CMS */}
      <PromoBannerStrip banners={banners} />

      {/* Install App banner — full width below the header */}
      <InstallAppPrompt />

      {/* HERO — 2-column layout matching mockup */}
      <section className="relative overflow-hidden">
        {/* soft background blobs */}
        <div className="pointer-events-none absolute inset-0 -z-10">
          <div className="absolute -top-32 -left-24 w-[28rem] h-[28rem] rounded-full bg-emerald-100/50 blur-3xl" />
          <div className="absolute -bottom-40 -right-24 w-[30rem] h-[30rem] rounded-full bg-orange-100/40 blur-3xl" />
        </div>

        <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 py-12 md:py-20">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16 items-center">
            {/* LEFT: copy */}
            <div className="text-center lg:text-left">
              {/* Orange van — RTL above headline */}
              <div className="overflow-hidden h-10 relative mb-3 lg:mx-0 max-w-md mx-auto" data-testid="animated-van-top">
                <div className="animated-van-rtl absolute">
                  <Truck className="w-9 h-9 text-[#F57C00] -scale-x-100" strokeWidth={1.5} />
                </div>
              </div>
              <h1
                className="text-4xl sm:text-5xl lg:text-6xl font-black tracking-tighter leading-[1.02]"
                style={{ fontFamily: "'Inter', sans-serif" }}
                data-testid="hero-headline"
              >
                <span className="text-[#050505]">{t('hero_headline_line1', "Ireland's Delivery")}</span>
                <br />
                <span style={{ color: '#21873E' }}>{t('hero_headline_line2', 'Marketplace')}</span><span style={{ color: '#F57E0E' }}>.</span>
              </h1>
              <p className="mt-5 text-lg sm:text-xl font-bold text-[#050505]">
                {t('hero_subheadline', 'Need something delivered? Let drivers bid for the job.')}
              </p>
              <p className="mt-2 text-base sm:text-lg text-[#4B5563] max-w-xl mx-auto lg:mx-0">
                {t('hero_text', 'Post your delivery for free, compare the bids you receive and choose the driver that suits you.')}
              </p>

              {/* CTAs */}
              <div className="mt-10 flex flex-col sm:flex-row items-center lg:items-start justify-center lg:justify-start gap-3">
                <button
                  onClick={() => navigate(t('hero_cta_primary_link', '/register'))}
                  className="cta-pulse inline-flex items-center justify-center px-7 py-4 text-base font-bold bg-[#1B5E20] text-white hover:bg-[#0e3f14] transition-all active:scale-95 gap-2 w-full sm:w-auto rounded-full shadow-lg shadow-emerald-900/10"
                  data-testid="hero-customer-cta"
                >
                  <Package className="w-5 h-5" /> {t('hero_cta_primary_text', 'Post a Delivery Job')}
                </button>
                <button
                  onClick={() => navigate(t('hero_cta_secondary_link', '/register'))}
                  className="inline-flex items-center justify-center px-7 py-4 text-base font-bold bg-white text-[#050505] border-2 border-gray-200 hover:border-gray-400 transition-all active:scale-95 gap-2 w-full sm:w-auto rounded-full"
                  data-testid="hero-driver-cta"
                >
                  <UserCheck className="w-5 h-5" /> {t('hero_cta_secondary_text', 'Sign Up to Get Started')}
                </button>
              </div>

              {/* Secondary CTA — Quick Post (no account required) */}
              <button
                onClick={() => navigate(t('hero_quickpost_link', '/quick-post'))}
                className="quick-post-pulse mt-4 w-full max-w-xl mx-auto lg:mx-0 flex items-center gap-3 px-4 py-3.5 bg-amber-50/70 border-2 border-amber-200 hover:border-amber-400 transition-all active:scale-[0.99] rounded-xl group text-left"
                data-testid="hero-quick-post-cta"
              >
                <span className="flex-shrink-0 w-9 h-9 rounded-full bg-amber-100 flex items-center justify-center">
                  <Zap className="w-5 h-5 text-amber-500 group-hover:text-amber-600" fill="currentColor" />
                </span>
                <span className="min-w-0 flex-1">
                  <span className="block text-sm sm:text-base font-bold text-[#050505]">
                    {t('hero_quickpost_title', 'Or use Quick Post — no account needed')}
                    <ArrowRight className="inline-block ml-1 w-4 h-4 text-[#F57C00] align-[-2px] transition-transform group-hover:translate-x-0.5" />
                  </span>
                  <span className="block text-xs text-gray-500 mt-0.5">
                    {t('hero_quickpost_text', 'Free to post. No subscription. Pay only when you accept an offer.')}
                  </span>
                </span>
              </button>
            </div>

            {/* RIGHT: explainer video (falls back to the hero image) */}
            <div className="relative">
              <div className="relative rounded-3xl overflow-hidden shadow-xl shadow-emerald-900/10 border border-gray-100 bg-gray-900">
                {heroVideo ? (
                  <>
                    <video
                      ref={heroVideoRef}
                      src={heroVideo}
                      poster={heroImage}
                      className="w-full aspect-video object-cover select-none"
                      autoPlay
                      muted
                      loop
                      playsInline
                      preload="metadata"
                      data-testid="hero-explainer-video"
                    />
                    <button
                      type="button"
                      onClick={toggleHeroSound}
                      aria-label={heroMuted ? 'Turn sound on' : 'Turn sound off'}
                      className="absolute bottom-4 right-4 w-10 h-10 rounded-full bg-black/55 hover:bg-black/75 backdrop-blur text-white flex items-center justify-center transition-colors"
                      data-testid="hero-video-sound-toggle"
                    >
                      {heroMuted ? <VolumeX className="w-4 h-4" /> : <Volume2 className="w-4 h-4" />}
                    </button>
                  </>
                ) : (
                  <img
                    src={heroImage}
                    alt="White delivery van with rear doors open, loaded with a sofa, boxes and a plant on an Irish street"
                    className="w-full h-[240px] sm:h-[320px] lg:h-[420px] object-cover select-none"
                    loading="eager"
                    draggable="false"
                    data-testid="hero-mockup-image"
                  />
                )}
                <span className="absolute top-4 right-4 px-3 py-1.5 rounded-full bg-white/95 backdrop-blur text-xs sm:text-sm font-bold text-[#1B5E20] shadow-sm">
                  {t('hero_image_caption', 'From small items to big deliveries')}
                </span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Feature strip — what makes JustDeliver different */}
      <FeatureStrip />

      {/* HOW JUSTDELIVER WORKS — 4 numbered steps */}
      <section className="py-20 md:py-28 bg-[#F9FAFB]">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
          <div className="text-center">
            <h2
              className="text-3xl sm:text-4xl lg:text-5xl font-black tracking-tight text-[#050505]"
              style={{ fontFamily: "'Inter', sans-serif" }}
            >
              {t('how_title_prefix', 'How')} <span className="text-[#2E7D32]">{t('how_title_brand', 'JustDeliver')}</span> {t('how_title_suffix', 'Works')}
            </h2>
            <p className="mt-3 text-base sm:text-lg text-[#4B5563]">
              {t('how_subtitle', 'Simple. Transparent. Built for Everyone.')}
            </p>
          </div>

          <div className="mt-14 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 lg:gap-8">
            {howSteps.map(({ num, title, desc, Visual }, idx) => (
              <div key={num} className="relative" data-testid={`how-step-${num}`}>
                <div className="bg-white border border-gray-200 rounded-2xl p-6 h-full shadow-sm hover:shadow-md hover:-translate-y-1 transition-all duration-300">
                  <div className="flex items-center gap-3 mb-4">
                    <span className="flex-shrink-0 w-10 h-10 rounded-full bg-[#2E7D32] text-white flex items-center justify-center text-base font-bold">
                      {num}
                    </span>
                    <h3 className="text-lg font-bold tracking-tight text-[#050505]">
                      {title}
                    </h3>
                  </div>
                  <Visual />
                  <p className="mt-4 text-sm text-[#4B5563] leading-relaxed">{desc}</p>
                </div>
                {/* arrow between cards on lg */}
                {idx < howSteps.length - 1 && (
                  <ArrowRight className="hidden lg:block absolute top-1/2 -right-5 -translate-y-1/2 w-6 h-6 text-[#2E7D32]" />
                )}
              </div>
            ))}
          </div>

          {/* CTAs */}
          <div className="mt-12 flex flex-col sm:flex-row items-center justify-center gap-4">
            <button
              onClick={() => navigate('/register')}
              className="inline-flex items-center justify-center px-8 py-4 text-base font-bold bg-[#1B5E20] text-white hover:bg-[#0e3f14] transition-all active:scale-95 gap-2 w-full sm:w-auto rounded-md"
              data-testid="hiw-customer-cta"
            >
              <Package className="w-5 h-5" /> Post a Job Now
            </button>
            <button
              onClick={() => navigate('/register')}
              className="inline-flex items-center justify-center px-8 py-4 text-base font-bold bg-white text-[#050505] border-2 border-gray-200 hover:border-gray-400 transition-all active:scale-95 gap-2 w-full sm:w-auto rounded-md"
              data-testid="hiw-driver-cta"
            >
              <Truck className="w-5 h-5" /> Start Driving
            </button>
          </div>
        </div>
      </section>

      {/* Drivers are bidding for your delivery */}
      <BiddingShowcase />

      {/* PROMINENT — Search for a Driver (public directory entry point) */}
      {DIRECTORY_ENABLED && <HomeDirectorySearch />}

      {/* What can you get delivered? */}
      <CategoryTiles />

      {/* Animated green van — LTR road */}
      <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 -mt-2 mb-6" data-testid="animated-van-green-row">
        <div className="overflow-hidden h-8 relative">
          <div className="absolute inset-x-0 top-1/2 border-t border-dashed border-gray-200"></div>
          <div className="animated-van absolute">
            <Truck className="w-7 h-7 text-[#2E7D32]" strokeWidth={1.5} />
          </div>
        </div>
      </div>

      {/* Live Delivery Activity — anonymised, refreshes every 30s */}
      <LiveActivityStrip />

      {/* TRUST / BENEFIT ICONS — 5 across */}
      <section className="py-20 md:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
          <div className="text-center mb-12">
            <span className="text-xs font-bold uppercase tracking-[0.2em] text-[#2E7D32]">{t('trust_eyebrow', 'Why JustDeliver')}</span>
            <h2
              className="mt-3 text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-[#050505]"
              style={{ fontFamily: "'Inter', sans-serif" }}
            >
              {t('trust_title', 'Safe, simple, and built for trust.')}
            </h2>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-6 lg:gap-4">
            {trust.map(({ Icon, title, desc }) => (
              <div
                key={title}
                className="flex flex-col items-center text-center px-3"
                data-testid={`trust-${title.toLowerCase().replace(/\s+/g, '-')}`}
              >
                <span className="w-14 h-14 rounded-full bg-[#2E7D32]/10 flex items-center justify-center">
                  <Icon className="w-7 h-7 text-[#2E7D32]" strokeWidth={1.75} />
                </span>
                <h4 className="mt-4 text-sm sm:text-base font-bold text-[#050505]">{title}</h4>
                <p className="mt-1.5 text-xs sm:text-sm text-[#4B5563] leading-relaxed">{desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Existing — Split Value Proposition (kept per user request) */}
      <section className="bg-[#FAFAFA]">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 py-16 md:py-20">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-10">
            {/* Customer Side */}
            <div className="bg-white border border-gray-200 p-8 md:p-10 rounded-xl hover:-translate-y-1 transition-transform duration-300 shadow-sm hover:shadow-lg text-left">
              <span className="text-xs font-bold uppercase tracking-[0.2em] text-[#4B5563]">{t('customers_eyebrow', 'For Customers')}</span>
              <h2
                className="mt-4 text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-[#050505]"
                style={{ fontFamily: "'Inter', sans-serif" }}
              >
                {t('customers_title', 'Get the Best Price, Fast')}
              </h2>
              <ul className="mt-6 space-y-4">
                {[
                  { icon: Send, text: 'Post your delivery and receive bids' },
                  { icon: DollarSign, text: 'Choose the best price' },
                  { icon: Eye, text: 'Track in real time' },
                ].map(({ icon: Icon, text }) => (
                  <li key={text} className="flex items-start gap-4">
                    <span className="flex-shrink-0 w-10 h-10 rounded-full bg-gray-100 flex items-center justify-center">
                      <Icon className="w-5 h-5 text-[#050505]" />
                    </span>
                    <span className="text-base leading-relaxed text-[#4B5563] pt-2">{text}</span>
                  </li>
                ))}
              </ul>
              <button
                onClick={() => navigate('/register')}
                className="mt-8 inline-flex items-center justify-center w-full px-8 py-4 text-base font-bold bg-[#050505] text-white hover:bg-gray-800 transition-all active:scale-95 gap-2"
                data-testid="split-customer-cta"
              >
                Post a Delivery <ArrowRight className="w-5 h-5" />
              </button>
            </div>

            {/* Driver Side */}
            <div className="bg-white border border-gray-200 p-8 md:p-10 rounded-xl hover:-translate-y-1 transition-transform duration-300 shadow-sm hover:shadow-lg text-left">
              <span className="text-xs font-bold uppercase tracking-[0.2em] text-[#4B5563]">{t('drivers_eyebrow', 'For Drivers')}</span>
              <h2
                className="mt-4 text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-[#050505]"
                style={{ fontFamily: "'Inter', sans-serif" }}
              >
                {t('drivers_title', 'Be Your Own Boss')}
              </h2>
              <ul className="mt-6 space-y-4">
                {[
                  { icon: MapPin, text: 'Choose your jobs' },
                  { icon: Handshake, text: 'Set your own prices' },
                  { icon: Clock, text: 'Earn on your schedule' },
                ].map(({ icon: Icon, text }) => (
                  <li key={text} className="flex items-start gap-4">
                    <span className="flex-shrink-0 w-10 h-10 rounded-full bg-[#2E7D32]/10 flex items-center justify-center">
                      <Icon className="w-5 h-5 text-[#2E7D32]" />
                    </span>
                    <span className="text-base leading-relaxed text-[#4B5563] pt-2">{text}</span>
                  </li>
                ))}
              </ul>
              <button
                onClick={() => navigate('/register')}
                className="mt-8 inline-flex items-center justify-center w-full px-8 py-4 text-base font-bold bg-[#2E7D32] text-white hover:bg-[#1B5E20] transition-all active:scale-95 gap-2"
                data-testid="split-driver-cta"
              >
                Start Earning <ArrowRight className="w-5 h-5" />
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Driver Types (kept) */}
      <section className="py-20 md:py-28">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 text-center">
          <span className="text-xs font-bold uppercase tracking-[0.2em] text-[#4B5563]">{t('vehicles_eyebrow', 'Deliver Your Way')}</span>
          <h2
            className="mt-4 text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-[#050505]"
            style={{ fontFamily: "'Inter', sans-serif" }}
          >
            {t('vehicles_title', 'Any Vehicle, Any Size')}
          </h2>
          <div className="mt-12 grid grid-cols-1 sm:grid-cols-3 gap-8">
            {[
              { icon: Car, label: 'Car Drivers', desc: 'Smaller, local deliveries', testId: 'driver-type-car' },
              { icon: Truck, label: 'Van Drivers', desc: 'Furniture & large items', testId: 'driver-type-van' },
              { icon: Container, label: 'Truck Drivers', desc: 'Pallets, machinery & house moves', testId: 'driver-type-truck' },
            ].map(({ icon: Icon, label, desc, testId }) => (
              <div
                key={label}
                className="bg-white border border-gray-200 p-8 rounded-xl hover:-translate-y-1 transition-transform duration-300 shadow-sm hover:shadow-lg"
                data-testid={testId}
              >
                <span className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-[#2E7D32]/10">
                  <Icon className="w-8 h-8 text-[#2E7D32]" />
                </span>
                <h3 className="mt-5 text-xl font-bold tracking-tight text-[#050505]" style={{ fontFamily: "'Inter', sans-serif" }}>
                  {label}
                </h3>
                <p className="mt-2 text-base text-[#4B5563]">{desc}</p>
              </div>
            ))}
          </div>
          <p className="mt-12 text-lg sm:text-xl leading-relaxed text-[#4B5563] max-w-2xl mx-auto">
            {t('vehicles_footnote', "Whether you're sending or delivering — JustDeliver makes it simple.")}
          </p>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-20 md:py-28 bg-[#050505]">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 text-center">
          <h2
            className="text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-white"
            style={{ fontFamily: "'Inter', sans-serif" }}
          >
            {t('final_title', 'Ready to get started?')}
          </h2>
          <p className="mt-4 text-lg text-gray-400 max-w-xl mx-auto">
            {t('final_text', "Post your first delivery job or sign up as a driver — it's free to start.")}
          </p>
          <div className="mt-10 flex flex-col sm:flex-row items-center justify-center gap-4">
            <button
              onClick={() => navigate('/register')}
              className="inline-flex items-center justify-center px-8 py-4 text-base font-bold bg-white text-[#050505] hover:bg-gray-200 transition-all active:scale-95 gap-2 w-full sm:w-auto"
              data-testid="footer-customer-cta"
            >
              <Package className="w-5 h-5" /> Post a Job
            </button>
            <button
              onClick={() => navigate('/register')}
              className="inline-flex items-center justify-center px-8 py-4 text-base font-bold bg-[#2E7D32] text-white hover:bg-[#1B5E20] transition-all active:scale-95 gap-2 w-full sm:w-auto"
              data-testid="footer-driver-cta"
            >
              <Truck className="w-5 h-5" /> Start Driving
            </button>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-8 border-t border-gray-100">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 flex flex-col sm:flex-row items-center justify-between gap-4">
          <img src="/logo.png" alt="JustDeliver" className="h-8 w-auto" />
          <div className="flex items-center gap-6">
            <a href="/terms" className="text-sm text-gray-500 hover:text-gray-900" data-testid="footer-terms-link">Terms & Conditions</a>
            <a href="/privacy" className="text-sm text-gray-500 hover:text-gray-900" data-testid="footer-privacy-link">Privacy Policy</a>
          </div>
          <SocialLinks />
          <p className="text-sm text-[#4B5563]">&copy; {new Date().getFullYear()} JustDeliver. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
}
